import { useEffect, useState } from "react";
import type { CalendarPickerLocation, CalendarSelection } from "@dentacore/shared-types";

import { getCalendarSelection, saveCalendarSelection } from "../api/calendar";
import { activeBranch } from "../api/tokens";
import { useActiveBranch } from "../api/use-active-branch";

/**
 * **Which locations the calendar shows** (CF-D1, CF-D12) — remembered on the
 * person's account, so the choice follows them between PCs.
 *
 * Read once and shared: the picker in the top bar writes it and the calendar
 * draws from it, and they are far apart in the tree.
 *
 * ── The active location is always one of them ───────────────────────────────
 *
 * Seeing several locations at once is a permission asked **at the active
 * location** (CF-D8), and every screen but the calendar still works in one
 * place. So the active location stays among those shown: ticking one location
 * makes it the active one (today's single calendar, unchanged), and a choice
 * that leaves the active one out moves the active location to the first one
 * ticked. A remembered choice that no longer includes the active location —
 * it was changed in Settings, say — shows the active location alone.
 */
let loaded: CalendarSelection | null = null;
let loading: Promise<CalendarSelection | null> | null = null;
const listeners = new Set<(value: CalendarSelection | null) => void>();

function publish(value: CalendarSelection | null): void {
  loaded = value;
  for (const listener of listeners) listener(value);
}

function load(): Promise<CalendarSelection | null> {
  loading ??= getCalendarSelection()
    .then((value) => {
      publish(value);
      return value;
    })
    // Not fatal: the calendar is the single location it always was.
    .catch(() => {
      loading = null;
      return null;
    });
  return loading;
}

/** The locations shown, worked out from the remembered choice and the active location. */
export function shownLocations(
  selection: CalendarSelection | null,
  active: string | null,
): string[] {
  if (active === null) return [];
  if (selection === null || !selection.canCombine) return [active];
  const reachable = new Set(selection.reachable.map((location) => location.id));
  const chosen = selection.branchIds.filter((id) => reachable.has(id));
  if (chosen.length < 2 || !chosen.includes(active)) return [active];
  // In the order the clinic created them, which is the order the picker lists.
  return selection.reachable.map((location) => location.id).filter((id) => chosen.includes(id));
}

export interface CalendarLocations {
  /** Null until the remembered choice has been read. */
  selection: CalendarSelection | null;
  /** Ids shown, oldest location first; one id is today's single calendar. */
  shown: string[];
  /** Everything the picker may offer, with codes and hues. */
  reachable: CalendarPickerLocation[];
  /**
   * Show these. One location makes it the active one and reloads, exactly as
   * the old switcher did; several are saved and drawn in place.
   */
  choose: (branchIds: readonly string[]) => Promise<void>;
}

export function useCalendarLocations(): CalendarLocations {
  const active = useActiveBranch();
  const [selection, setSelection] = useState<CalendarSelection | null>(loaded);

  useEffect(() => {
    setSelection(loaded);
    listeners.add(setSelection);
    void load();
    return () => {
      listeners.delete(setSelection);
    };
  }, []);

  const choose = async (branchIds: readonly string[]): Promise<void> => {
    const ids = [...new Set(branchIds)];
    if (ids.length === 0) return;
    // **Shown at once, saved behind it**: a tick that waited for the server
    // read as a click that did nothing. A save that fails puts it back.
    const before = loaded;
    if (before !== null) publish({ ...before, branchIds: ids.length === 1 ? [] : ids });
    try {
      await saveAndApply(ids);
    } catch (cause) {
      publish(before);
      throw cause;
    }
  };

  const saveAndApply = async (ids: string[]): Promise<void> => {
    if (ids.length === 1) {
      // One location is today's calendar: remember nothing, make it active.
      publish(await saveCalendarSelection([]));
      if (ids[0] !== active) {
        activeBranch.set(ids[0] ?? null);
        window.location.reload();
      }
      return;
    }
    publish(await saveCalendarSelection(ids));
    if (active === null || !ids.includes(active)) {
      const first = (loaded?.reachable ?? []).find((location) => ids.includes(location.id));
      activeBranch.set(first?.id ?? ids[0] ?? null);
      window.location.reload();
    }
  };

  return {
    selection,
    shown: shownLocations(selection, active),
    reachable: selection?.reachable ?? [],
    choose,
  };
}
